import { createSignal, For, Show, createMemo, type JSX } from "solid-js"
import { useNavigate } from "@solidjs/router"
import { DragDropProvider, PointerSensor } from "@dnd-kit/solid"
import { isSortable, useSortable } from "@dnd-kit/solid/sortable"
import { Accessibility, AutoScroller, Feedback, PointerActivationConstraints } from "@dnd-kit/dom"
import { RestrictToVerticalAxis } from "@dnd-kit/abstract/modifiers"
import { RestrictToElement } from "@dnd-kit/dom/modifiers"
import { useTabs, type Tab, tabKey, tabHref } from "@/context/tabs"
import { useLanguage } from "@/context/language"
import { getRelativeTime } from "@/utils/time"
import "@/components/zd-left-sidebar.css"

function Chevron(props: { expanded: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      class="w-[14px] h-[14px] shrink-0 transition-transform"
      style={{ transform: props.expanded ? "rotate(90deg)" : "rotate(0deg)" }}
    >
      <path d="M6 4L10 8L6 12" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  )
}

// 最近对话单条：竖向可拖拽排序（dnd-kit），拖拽结束后点击会被 preventDefault 拦截，不触发导航。
function RecentConversationRow(props: { tab: Tab; index: () => number; title: string; time?: string }): JSX.Element {
  const navigate = useNavigate()
  const sortable = useSortable({
    get id() {
      return tabKey(props.tab)
    },
    get index() {
      return props.index()
    },
  })

  return (
    <button
      ref={sortable.ref}
      class="zd-recent-conv__item"
      classList={{ "zd-recent-conv__item--dragging": sortable.isDragSource() }}
      onClick={(event) => {
        // 拖拽结束后的那次 click 会被 dnd-kit preventDefault，此处跳过，避免重排后误跳转。
        if (event.defaultPrevented) return
        navigate(tabHref(props.tab))
      }}
    >
      <span class="w-1 h-1 rounded-full bg-v2-text-text-tertiary shrink-0" />
      <span class="flex-1 min-w-0 truncate">{props.title}</span>
      <Show when={props.time}>
        <span class="zd-conv-time">{props.time}</span>
      </Show>
    </button>
  )
}

export function ZdRecentConversations(): JSX.Element {
  const tabs = useTabs()
  const language = useLanguage()

  const [expanded, setExpanded] = createSignal(true)

  // tabs.info 里记录的会话最后更新时间（ms）。旧持久化数据没有该字段，返回 0 让其沉到列表末尾。
  const tabUpdated = (tab: Tab) => tabs.info[tabKey(tab)]?.updated ?? 0

  // 排序规则：
  // - 手动拖拽过的会话带持久化的 customOrder（0 为最前），固定在列表顶部、不再随更新时间浮动；
  // - 没有 customOrder 的会话（新打开的/旧数据）按 updated 倒序，排在自定义块之前；
  //   若其数量恰好等于 customOrder 的空缺位数，说明这些就是当初那批条目（比如会话被
  //   关闭重开后 info 被重建、customOrder 丢失），此时并入自定义块、按空缺位原位恢复。
  // Array#sort 是稳定排序，同组内保持 tabs.store 相对顺序。
  const sessionTabs = createMemo(() => {
    const all = tabs.store.filter((t: Tab) => t.type === "session")
    const ordered = all.filter((t) => tabs.info[tabKey(t)]?.customOrder !== undefined)
    const rest = all.filter((t) => tabs.info[tabKey(t)]?.customOrder === undefined)
    ordered.sort((a, b) => tabs.info[tabKey(a)]!.customOrder! - tabs.info[tabKey(b)]!.customOrder!)
    rest.sort((a, b) => tabUpdated(b) - tabUpdated(a))
    const maxOrder = ordered.length ? tabs.info[tabKey(ordered[ordered.length - 1])]!.customOrder! + 1 : 0
    if (ordered.length > 0 && rest.length > 0 && rest.length <= maxOrder - ordered.length) {
      const taken = new Set(ordered.map((t) => tabs.info[tabKey(t)]!.customOrder!))
      const result: (Tab | undefined)[] = [...ordered]
      for (const tab of rest) {
        let slot = 0
        while (taken.has(slot)) slot++
        taken.add(slot)
        result[slot] = tab
      }
      return result.filter((t): t is Tab => t !== undefined)
    }
    return [...rest, ...ordered]
  })

  const tabTitle = (tab: Tab) => {
    if (tab.type === "session") {
      const info = tabs.info[tabKey(tab)]
      return info?.title || "新对话"
    }
    return "新对话"
  }

  // tabs.info 里记录的会话最后更新时间。旧持久化数据没有该字段时返回 undefined，该项不显示时间。
  const tabTime = (tab: Tab) => {
    const updated = tabs.info[tabKey(tab)]?.updated
    if (updated === undefined) return
    return getRelativeTime(new Date(updated).toISOString(), language.t)
  }

  // 手动拖拽重排：把排序后的可视顺序写回 tabs.store（会话段整体重排，其他类型标签
  // 保持原位），并把可视顺序固化为每个会话的 customOrder，持久化到 tabs.info。
  // 之后这批会话固定按手动顺序展示，不再随更新时间浮动。
  const reorderSessions = (activeKey: string, toIndex: number) => {
    const ordered = sessionTabs().map((tab) => tabKey(tab))
    const fromIndex = ordered.indexOf(activeKey)
    if (fromIndex === -1 || fromIndex === toIndex) return
    ordered.splice(toIndex, 0, ...ordered.splice(fromIndex, 1))
    const queue = [...ordered]
    const keys = tabs.store.map((tab) => (tab.type === "session" ? (queue.shift() ?? tabKey(tab)) : tabKey(tab)))
    tabs.reorder(keys)
    tabs.setSessionCustomOrder(ordered)
  }

  let listRef: HTMLDivElement | undefined

  return (
    <div class="zd-recent-conv">
      <button
        class="zd-recent-conv__header"
        onClick={() => setExpanded((v) => !v)}
      >
        <Chevron expanded={expanded()} />
        <span class="text-left">最近对话</span>
        <Show when={sessionTabs().length > 0}>
          <span class="tabular-nums">
            （{sessionTabs().length}）
          </span>
        </Show>
        <span class="flex-1" />
      </button>

      <Show when={expanded()}>
        <div ref={listRef} class="zd-recent-conv__list">
          <Show
            when={sessionTabs().length > 0}
            fallback={<div class="px-2 py-1 text-[12px] text-v2-text-text-tertiary">暂无对话</div>}
          >
            <DragDropProvider
              sensors={(defaults) => [
                ...defaults.filter((sensor) => sensor !== PointerSensor),
                PointerSensor.configure({
                  activationConstraints: (event) =>
                    event.pointerType === "touch"
                      ? [new PointerActivationConstraints.Delay({ value: 250, tolerance: 5 })]
                      : [new PointerActivationConstraints.Distance({ value: 4 })],
                }),
              ]}
              modifiers={[RestrictToVerticalAxis, RestrictToElement.configure({ element: () => listRef })]}
              plugins={(defaults) => [
                ...defaults.filter((plugin) => plugin !== AutoScroller && plugin !== Feedback),
                AutoScroller.configure({ acceleration: 8, threshold: { x: 0, y: 0.05 } }),
                Feedback.configure({ dropAnimation: null }),
              ]}
              onDragEnd={(event) => {
                const source = event.operation.source
                if (event.canceled || !isSortable(source)) return
                if (source.initialIndex !== source.index) reorderSessions(source.id.toString(), source.index)
              }}
            >
              <For each={sessionTabs()}>
                {(tab, index) => (
                  <RecentConversationRow tab={tab} index={index} title={tabTitle(tab)} time={tabTime(tab)} />
                )}
              </For>
            </DragDropProvider>
          </Show>
        </div>
      </Show>
    </div>
  )
}
